// You: your name and photo over four tabs. Stats: streak, average, best, where you finish, your
// phoenixes, your form against the solver, your ranks and your words. Badges: your achievements by
// category (tap one for the list), your profile and the badge beside your name; a guest's wait,
// unclaimed, for them to sign in. History: a month at a time (tap a day to review it, or play one you missed) and your
// latest plays. Settings: photo, name, account, theme, sound, the daily reminder and home screen,
// how to play and feedback.
import { useRef, useState, type FormEvent } from 'react'
import { Link, Navigate, NavLink, useNavigate, useParams } from 'react-router'
import type { BadgeCategory, CalendarDay, Me, Stats, StatsForm } from '@wbwl/contract'
import { BadgeGrid, BadgePicker, CategorySheet, PhoenixCrown } from '../components/achievements-ui.tsx'
import { Badge, badgeLabel } from '../components/Badge.tsx'
import { editionNumber, RANK_STEPS } from '@wbwl/rules'
import { HowTo } from '../components/HowTo.tsx'
import { Icon } from '../components/Icon.tsx'
import { ReminderSettings } from '../components/reminder-ui.tsx'
import { applyTheme, Avatar, currentTheme, ErrorNote, Loading, Sheet, TabBar, useToast, type ThemeChoice } from '../components/shell.tsx'
import { ApiFailure, api } from '../lib/api.ts'
import { useEdition, useMe, useMyAchievements, useStats } from '../lib/data.ts'
import { dayDate, fmt } from '../lib/game.ts'
import { squarePhoto } from '../lib/photo.ts'
import { setSoundOn, soundOn } from '../lib/sound.ts'
import { invalidate, setResource } from '../lib/store.ts'

const TABS = [
  { tab: 'stats', label: 'Stats', to: '/you' },
  { tab: 'achievements', label: 'Badges', to: '/you/achievements' },
  { tab: 'history', label: 'History', to: '/you/history' },
  { tab: 'settings', label: 'Settings', to: '/you/settings' },
] as const
// Badges on the guest's "Sign in for a profile" card, locked, in its blue.
const GHOSTS: readonly BadgeCategory[] = ['cub', 'firekeeper', 'regular', 'headliner']

export default function YouPage() {
  const param = useParams().tab
  const tab = param ?? 'stats'
  const me = useMe()
  const stats = useStats()
  if (!TABS.some((t) => t.tab === tab) || param === 'stats') return <Navigate to="/you" replace />
  if (me.error) return <div className="page"><ErrorNote error={me.error} retry={() => me.reload()} /></div>
  if (!me.data) return <div className="page"><Loading label="Looking you up…" /></div>
  const m = me.data
  const first = stats.data?.history.filter((d) => d.ranked).at(-1)
  const head = (
    <>
      <Avatar id={m.id} name={m.displayName} src={m.avatar} size={52} />
      <div className="who">
        <b className="ellipsis">{m.displayName}</b>
        <span className="faint mono small">
          {m.kind === 'guest' ? 'Guest · playing on this device' : first ? <>{fmt(stats.data!.played)} edition{stats.data!.played === 1 ? '' : 's'} · <span className="nowrap">since No. {first.edition}</span></> : m.email ?? 'Signed in'}
        </span>
      </div>
    </>
  )
  return (
    <div className="page you">
      <header className="profile">
        {head}
        {/* A member's public profile, a button like any other so it reads as one. */}
        {m.profile && <Link to={`/p/${m.profile}`} className="btn ghost sm to-profile"><Icon name="user" size={16} />My Profile</Link>}
      </header>
      {m.kind === 'guest' && (
        <Link to="/signin" className="signin-nudge"><Icon name="lock" size={20} /><span>{m.streak > 0 ? `Save your ${m.streak}-day streak` : 'Save your games'} — sign in so they follow you to any device.</span><Icon name="next" size={18} /></Link>
      )}
      <nav className="seg you-tabs" aria-label="You">
        {TABS.map((t) => <NavLink key={t.tab} to={t.to} end className={({ isActive }) => (isActive ? 'on' : '')}>{t.label}</NavLink>)}
      </nav>
      {tab === 'settings' ? <Settings me={m} /> : tab === 'achievements' ? <BadgesTab me={m} /> : stats.error ? <ErrorNote error={stats.error} retry={() => stats.reload()} /> : !stats.data ? <Loading label="Adding it up…" /> : tab === 'history' ? <History stats={stats.data} /> : <StatsTab stats={stats.data} />}
      <TabBar />
    </div>
  )
}

// ---------- stats ----------

function StatsTab({ stats }: { stats: Stats }) {
  return (
    <>
      <div className="stat-grid">
        <div className="stat"><b>{stats.streak}</b><span>day streak<br />best {stats.bestStreak}</span></div>
        <div className="stat"><b>{stats.average !== null ? fmt(stats.average) : '—'}</b><span>average<br />{stats.played} played</span></div>
        <div className="stat"><b>{stats.best ? fmt(stats.best.total) : '—'}</b><span>best score<br />{stats.best ? `No. ${stats.best.edition}` : 'not yet'}</span></div>
      </div>
      {stats.played > 0 && (
        <>
          <div className="stat-grid">
            {stats.bestFinish ? (
              <Link className="stat" to={`/results/${stats.bestFinish.edition}`}><b>#{fmt(stats.bestFinish.position)}</b><span>best finish<br />No. {stats.bestFinish.edition}</span></Link>
            ) : <div className="stat"><b>—</b><span>best finish<br />not yet</span></div>}
            <div className="stat"><b>{stats.usualTopPercent !== null ? <><small>top</small>{stats.usualTopPercent}%</> : '—'}</b><span>your usual<br />finish</span></div>
            <div className="stat"><b>{stats.phoenix.raised}</b><span>phoenixes<br />raised of {stats.phoenix.tries}</span></div>
          </div>
          <Form form={stats.form} />
        </>
      )}
      <div className="sec-head you-head"><h3>Your ranks</h3><span className="eyebrow">{stats.played} edition{stats.played === 1 ? '' : 's'}</span></div>
      <RankBars counts={stats.rankCounts} />
      {stats.bestWords.length > 0 && (
        <>
          <div className="sec-head you-head"><h3>Your words</h3><span className="eyebrow">All time</span></div>
          <div className="card list word-facts">
            {stats.opener && <div className="row"><span>Go-to opener</span><span className="val"><b className="word">{stats.opener.word}</b><small>{stats.opener.times} times</small></span></div>}
            <div className="row"><span>Different words</span><span className="val"><b>{fmt(stats.differentWords)}</b><small>of {fmt(stats.wordsPlayed)} played</small></span></div>
          </div>
          <div className="sub-label">Highest scoring</div>
          <div className="card list">
            {stats.bestWords.map((w) => (
              <Link className="row best-word" key={`${w.word}${w.edition}`} to={`/results/${w.edition}`}>
                <span className="mono word">{w.word}</span><span className="faint mono small">No. {w.edition}</span><b className="mono">{w.total}</b>
              </Link>
            ))}
          </div>
        </>
      )}
    </>
  )
}

/**
 * Your share of the solver's best: a bar per edition for the last 30, coloured like the History
 * calendar. A profile shows someone else's (`own` false: no links to your results).
 */
export function Form({ form, title = 'Your form', own = true }: { form: StatsForm; title?: string; own?: boolean }) {
  const delta = form.average !== null && form.before !== null ? form.average - form.before : null
  return (
    <>
      <div className="sec-head you-head"><h3>{title}</h3><span className="eyebrow">Last 30 days</span></div>
      <div className="card form-card">
        <div className="form-top">
          <b className="pct">{form.average !== null ? `${form.average}%` : '—'}</b>
          <span className="of">of the solver’s<br />best, on average</span>
          {delta !== null && (
            <div className="delta"><b className={delta < 0 ? 'down' : ''}>{delta === 0 ? '±0' : `${delta > 0 ? '▲' : '▼'} ${Math.abs(delta)}`}</b><span>on the 30 days before</span></div>
          )}
        </div>
        <div className="form-chart" role="img" aria-label={`${own ? 'Your' : 'Their'} share of the solver’s best on each of No. ${form.from} to No. ${form.from + form.days.length - 1}`}>
          {form.days.map((d, i) => (d ? <i key={i} className={rankClass(d.rankIndex)} style={{ height: `${Math.min(d.percent, 100)}%` }} /> : <i key={i} className="miss" />))}
          {form.average !== null && <span className="avg" style={{ bottom: `${Math.min(form.average, 100)}%` }} />}
        </div>
        <div className="form-axis"><span>No. {form.from}</span><span>Today</span></div>
        <div className="form-foot">
          <span>{form.allTime !== null && <>All time <b>{form.allTime}%</b></>}</span>
          {form.best && (own
            ? <Link to={`/results/${form.best.edition}`}>Best <b>{form.best.percent}%</b> · No. {form.best.edition}</Link>
            : <span>Best <b>{form.best.percent}%</b> · No. {form.best.edition}</span>)}
        </div>
      </div>
    </>
  )
}

function RankBars({ counts }: { counts: readonly { name: string; count: number }[] }) {
  const max = Math.max(1, ...counts.map((c) => c.count))
  return (
    <div className="rank-bars">
      {[...counts].reverse().filter((c) => c.name !== RANK_STEPS[0].name || c.count > 0).map((c, i) => (
        <div className="rk" key={c.name}><span className="nm">{c.name}</span><span className="bar" style={{ width: `${(c.count / max) * 100}%`, background: i === 0 ? 'var(--gold)' : undefined }} /><span className="c">{c.count}</span></div>
      ))}
    </div>
  )
}

// ---------- badges ----------

function BadgesTab({ me }: { me: Me }) {
  const q = useMyAchievements()
  const [open, setOpen] = useState<BadgeCategory | null>(null)
  const [picking, setPicking] = useState(false)
  if (q.error) return <ErrorNote error={q.error} retry={() => q.reload()} />
  if (!q.data) return <Loading label="Polishing your badges…" />
  const a = q.data
  const guest = me.kind === 'guest'
  const earned = a.categories.reduce((n, c) => n + c.earned, 0)
  const total = a.categories.reduce((n, c) => n + c.total, 0)
  const phoenix = a.categories.find((c) => c.id === 'phoenix')
  return (
    <>
      {guest ? (
        <div className="get-profile">
          <div className="ttl"><Icon name="lock" />Sign in for a profile</div>
          <p>A page anyone can open with your badges and stats, a link to share, and a badge beside your name once one reaches Silver. Your games{earned > 0 ? <> and your <b>{earned} unclaimed achievement{earned === 1 ? '' : 's'}</b></> : null} come with you.</p>
          <div className="ghosts" aria-hidden="true">{GHOSTS.map((c) => <Badge key={c} category={c} tier="locked" size={40} ghost />)}</div>
          <Link className="btn ink block" to="/signin">Sign in</Link>
        </div>
      ) : (
        <div className="card list you-badge">
          <div className="set">
            {a.badge ? <Badge category={a.badge.category} tier={a.badge.tier} size={26} /> : <Icon name="sparkle" />}
            <div className="lbl"><b>Beside your name</b><small>{a.badge ? badgeLabel(a.badge.category, a.badge.tier) : a.choice === 'none' ? 'No badge' : 'Reach Silver in any category'}</small></div>
            <button type="button" className="btn ghost sm" onClick={() => setPicking(true)}>Change</button>
          </div>
        </div>
      )}
      <div className="sec-head you-head"><h3>Your badges</h3><span className="eyebrow">{earned} of {total} achievements</span></div>
      {phoenix && <PhoenixCrown category={phoenix} shown={a.badge?.category === 'phoenix'} onOpen={() => setOpen('phoenix')} />}
      <BadgeGrid categories={a.categories} shown={a.badge?.category ?? null} onOpen={setOpen} />
      <p className="faint small center you-badge-note">{guest ? 'Unclaimed until you sign in.' : 'Tap a badge to see every achievement in it.'}</p>
      <CategorySheet category={a.categories.find((c) => c.id === open) ?? null} all={a.categories} owner={null} onClose={() => setOpen(null)} />
      {!guest && <BadgePicker open={picking} onClose={() => setPicking(false)} data={a} />}
    </>
  )
}

// ---------- history ----------

const pad = (n: number) => String(n).padStart(2, '0')
const monthName = (month: string) => new Date(`${month}-15T12:00:00Z`).toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' })
function shiftMonth(month: string, by: number): string {
  const [y, m] = month.split('-').map(Number)
  const d = new Date(Date.UTC(y, m - 1 + by, 1))
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}`
}
const rankClass = (rankIndex: number) => (rankIndex >= 6 ? 'r5' : rankIndex >= 5 ? 'r4' : rankIndex >= 4 ? 'r3' : rankIndex >= 2 ? 'r2' : 'r1')

function History({ stats }: { stats: Stats }) {
  const today = useEdition('today').data
  const navigate = useNavigate()
  const [month, setMonth] = useState<string | null>(null)
  if (!today) return <Loading label="Adding it up…" />
  const thisMonth = today.date.slice(0, 7)
  const shown = month ?? thisMonth
  // Back as far as your first play, and never past this month.
  const earliest = stats.history.at(-1)?.date.slice(0, 7) ?? thisMonth
  const byDate = new Map(stats.history.map((d) => [d.date, d]))
  const days = new Date(Date.UTC(Number(shown.slice(0, 4)), Number(shown.slice(5)), 0)).getUTCDate()
  const lead = new Date(`${shown}-01T12:00:00Z`).getUTCDay()

  function open(date: string, play: CalendarDay | undefined) {
    if (play) navigate(`/results/${play.edition}`)
    else navigate(date === today!.date ? '/play' : `/play/${editionNumber(date)}`)
  }

  return (
    <>
      <div className="month-head">
        <h3>{monthName(shown)}</h3>
        <button type="button" className="ibtn boxed" aria-label="Earlier month" disabled={shown <= earliest} onClick={() => setMonth(shiftMonth(shown, -1))}><Icon name="back" /></button>
        <button type="button" className="ibtn boxed" aria-label="Later month" disabled={shown >= thisMonth} onClick={() => setMonth(shiftMonth(shown, 1))}><Icon name="next" /></button>
      </div>
      <div className="cal you-cal">
        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => <span key={i} className="cal-dow">{d}</span>)}
        {Array.from({ length: lead }, (_, i) => <i key={`x${i}`} className="x" />)}
        {Array.from({ length: days }, (_, i) => {
          const date = `${shown}-${pad(i + 1)}`
          const play = byDate.get(date)
          if (date > today.date || editionNumber(date) < 0) return <i key={date} className="fut">{i + 1}</i>
          const cls = play ? `${rankClass(play.rankIndex ?? 0)}${play.ranked ? '' : ' archive'}` : 'miss'
          return (
            <button key={date} type="button" className={`${cls}${date === today.date ? ' today' : ''}`} title={`No. ${editionNumber(date)}${play ? ` · ${fmt(play.total!)}` : ''}`} onClick={() => open(date, play)}>
              {i + 1}
            </button>
          )
        })}
      </div>
      <div className="cal-key faint mono small"><span>Draft</span><span className="sw"><i className="r1" /><i className="r2" /><i className="r3" /><i className="r4" /><i className="r5" /></span><span>Stop the Presses</span><span className="hint">tap a day</span></div>

      {stats.history.length > 0 && (
        <>
          <div className="sec-head you-head"><h3>Latest</h3><span className="eyebrow">Newest first</span></div>
          <div className="card list">
            {stats.history.slice(0, 5).map((d) => (
              <Link key={d.edition} className="row play-row" to={`/results/${d.edition}`}>
                <div className="two"><b>No. {d.edition}</b><span>{dayDate(d.date)}{d.ranked ? '' : ' · archive'}</span></div>
                <span className="rk-name">{RANK_STEPS[d.rankIndex ?? 0]?.name}</span>
                <b>{fmt(d.total!)}</b>
                <Icon name="next" size={16} />
              </Link>
            ))}
          </div>
        </>
      )}
    </>
  )
}

// ---------- settings ----------

const SOURCE_NAME = { google: 'From your Google account', gravatar: 'From Gravatar', upload: 'Uploaded' } as const

function Settings({ me }: { me: Me }) {
  const [theme, setTheme] = useState<ThemeChoice>(currentTheme)
  const [sound, setSound] = useState(soundOn)
  const [photo, setPhoto] = useState(false)
  const [naming, setNaming] = useState(false)
  const [howto, setHowto] = useState(false)
  const [feedback, setFeedback] = useState(false)
  const toast = useToast()
  const navigate = useNavigate()

  async function signOut() {
    await api.post('/auth/logout').catch(() => {})
    invalidate('me', 'play:', 'stats', 'board:', 'thread:', 'mychallenges', 'rivals', 'achievements', 'profile:')
    navigate('/')
    toast('Signed out. You’re playing as a new guest.')
  }

  return (
    <>
      <div className="eyebrow set-group">Profile</div>
      <div className="card list">
        {me.kind === 'guest' ? (
          <div className="set"><Avatar id={me.id} name={me.displayName} src={me.avatar} size={36} /><div className="lbl"><b>Photo</b><small>Your initials</small></div><Link to="/signin" className="btn ghost sm">Sign in to add one</Link></div>
        ) : (
          <div className="set"><Avatar id={me.id} name={me.displayName} src={me.avatar} size={36} /><div className="lbl"><b>Photo</b><small>{me.avatarSource ? SOURCE_NAME[me.avatarSource] : 'Your initials'}</small></div><button type="button" className="btn ghost sm" onClick={() => setPhoto(true)}>Change</button></div>
        )}
        {naming ? <NameForm me={me} onDone={() => setNaming(false)} /> : (
          <div className="set"><div className="lbl"><b>Name</b><small>{me.displayName} · shown on leaderboards</small></div><button type="button" className="btn ghost sm" onClick={() => setNaming(true)}>Edit</button></div>
        )}
        {me.kind === 'guest' ? (
          <div className="set"><div className="lbl"><b>Account</b><small>Guest · playing on this device</small></div><Link to="/signin" className="btn ink sm">Sign in</Link></div>
        ) : (
          <div className="set"><div className="lbl"><b>Account</b><small>{me.providers.includes('google') ? 'Google' : 'Email'} · {me.email ?? 'signed in'}</small></div><button type="button" className="btn ghost sm" onClick={signOut}>Sign out</button></div>
        )}
      </div>

      <div className="eyebrow set-group">Game</div>
      <div className="card list">
        <div className="set">
          <div className="lbl"><b>Theme</b></div>
          <div className="seg small-seg">
            {(['auto', 'paper', 'night'] as const).map((t) => <button key={t} type="button" className={theme === t ? 'on' : ''} onClick={() => { applyTheme(t); setTheme(t) }}>{t === 'auto' ? 'Auto' : t === 'paper' ? 'Paper' : 'Night'}</button>)}
          </div>
        </div>
        <div className="set">
          <div className="lbl"><b>Sound</b></div>
          <div className="seg small-seg">
            {([true, false] as const).map((on) => <button key={String(on)} type="button" className={sound === on ? 'on' : ''} onClick={() => { setSoundOn(on); setSound(on) }}>{on ? 'On' : 'Off'}</button>)}
          </div>
        </div>
      </div>

      <ReminderSettings />

      <div className="eyebrow set-group">Help</div>
      <div className="card list">
        <button type="button" className="set" onClick={() => setHowto(true)}><Icon name="help" /><div className="lbl"><b>How to play</b></div><Icon name="next" size={16} /></button>
        <button type="button" className="set" onClick={() => setFeedback(true)}><Icon name="message" /><div className="lbl"><b>Send feedback</b></div><Icon name="next" size={16} /></button>
        {me.admin && <Link className="set" to="/admin"><Icon name="settings" /><div className="lbl"><b>Admin</b></div><Icon name="next" size={16} /></Link>}
      </div>
      <p className="faint small legal"><a href="/privacy">Privacy policy</a> · <a href="mailto:privacy@lettersonfire.com">privacy@lettersonfire.com</a></p>

      <PhotoSheet me={me} open={photo} onClose={() => setPhoto(false)} />
      <FeedbackSheet open={feedback} onClose={() => setFeedback(false)} />
      <HowTo open={howto} onClose={() => setHowto(false)} />
    </>
  )
}

function NameForm({ me, onDone }: { me: Me; onDone(): void }) {
  const [name, setName] = useState(me.displayName)
  const toast = useToast()
  async function save(e: FormEvent) {
    e.preventDefault()
    try {
      const updated = await api.patch<Me>('/me', { displayName: name.trim() })
      setResource('me', updated)
      invalidate('board:', 'thread:', 'chat:', 'challenge:', 'edchallenges:', 'mychallenges', 'rivals', 'rivalry:')
      onDone()
      toast(<><Icon name="check" />Name saved</>)
    } catch (err) {
      toast(err instanceof ApiFailure && err.body.error === 'invalid_name' ? '2–24 letters, numbers, spaces, dots, dashes or underscores' : 'Couldn’t save your name')
    }
  }
  return (
    <form className="set name-form" onSubmit={save}>
      <input value={name} onChange={(e) => setName(e.target.value)} maxLength={24} autoFocus aria-label="Display name" />
      <button className="btn ink sm" type="submit">Save</button>
      <button className="btn plain sm" type="button" onClick={onDone}>Cancel</button>
    </form>
  )
}

/** Your profile photo: upload one, bring in your Google photo or Gravatar, or go back to initials. */
function PhotoSheet({ me, open, onClose }: { me: Me; open: boolean; onClose(): void }) {
  const toast = useToast()
  const [busy, setBusy] = useState(false)
  const picker = useRef<HTMLInputElement>(null)

  async function change(request: () => Promise<Me>, done: string) {
    setBusy(true)
    try {
      setResource('me', await request())
      // Anywhere your face shows: leaderboards, comments, challenges, records.
      invalidate('board:', 'thread:', 'chat:', 'challenge:', 'edchallenges:', 'mychallenges', 'rivals', 'rivalry:')
      toast(<><Icon name="check" />{done}</>)
      onClose()
    } catch (err) {
      toast(err instanceof ApiFailure && err.body.message ? err.body.message : 'Couldn’t change your photo. Try again.')
    } finally {
      setBusy(false)
    }
  }
  async function upload(file: File) {
    let image: string
    try { image = await squarePhoto(file) } catch { return toast('Couldn’t read that picture. Try a JPEG or PNG.') }
    await change(() => api.post<Me>('/me/avatar', { image }), 'Photo saved')
  }

  return (
    <Sheet open={open} onClose={onClose} label="Your photo">
      <div className="share">
        <div className="share-head">
          <h2>Your photo</h2>
          <button type="button" className="ibtn boxed" aria-label="Close" onClick={onClose}><Icon name="close" /></button>
        </div>
        <div className="photo-now"><Avatar id={me.id} name={me.displayName} src={me.avatar} size={56} /><span className="faint mono small">{me.avatarSource ? SOURCE_NAME[me.avatarSource] : 'Your initials'}</span></div>
        <input ref={picker} type="file" accept="image/*" hidden onChange={(e) => { const file = e.target.files?.[0]; e.target.value = ''; if (file) void upload(file) }} />
        <button type="button" className="btn ink block" disabled={busy} onClick={() => picker.current?.click()}><Icon name="image" />Upload a photo</button>
        {me.providers.includes('google') && me.avatarSource !== 'google' && (
          <button type="button" className="btn ghost block" disabled={busy} onClick={() => change(() => api.post<Me>('/me/avatar', { source: 'google' }), 'Using your Google photo')}>Use your Google photo</button>
        )}
        {me.avatarSource !== 'gravatar' && (
          <button type="button" className="btn ghost block" disabled={busy} onClick={() => change(() => api.post<Me>('/me/avatar', { source: 'gravatar' }), 'Using your Gravatar')}>Use your Gravatar</button>
        )}
        {me.avatar && <button type="button" className="btn plain" disabled={busy} onClick={() => change(() => api.del<Me>('/me/avatar'), 'Photo removed')}>Remove photo</button>}
      </div>
    </Sheet>
  )
}

function FeedbackSheet({ open, onClose }: { open: boolean; onClose(): void }) {
  const [body, setBody] = useState('')
  const toast = useToast()
  async function send(e: FormEvent) {
    e.preventDefault()
    if (!body.trim()) return
    try {
      await api.post('/feedback', { body: body.trim() })
      setBody('')
      onClose()
      toast(<><Icon name="check" />Thanks — we read every note</>)
    } catch {
      toast('Couldn’t send that. Try again later.')
    }
  }
  return (
    <Sheet open={open} onClose={onClose} label="Send feedback">
      <form className="share feedback" onSubmit={send}>
        <div className="share-head">
          <h2>Send feedback</h2>
          <button type="button" className="ibtn boxed" aria-label="Close" onClick={onClose}><Icon name="close" /></button>
        </div>
        <textarea value={body} onChange={(e) => setBody(e.target.value)} maxLength={2000} rows={4} placeholder="Ideas, bugs, a word we’re missing…" aria-label="Your note" autoFocus />
        <button className="btn ink block" type="submit" disabled={!body.trim()}>Send</button>
      </form>
    </Sheet>
  )
}
